import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { AccordionSection } from "./AccordionSection";
import { SectionErrors } from "./SectionErrors";
import { SignedImage } from "../../common/SignedImage";

const IMAGE_GROUPS = [
  {
    title: "Boiler",
    types: ["Boiler (Front)", "Boiler (Underneath)", "Boiler Filter", "Data Badge Serial Number", "Filter Badge Serial Number"],
  },
  {
    title: "Flue and Condensate",
    types: [
      "Boiler Flue",
      "Flue Sealed",
      "Flue Termination",
      "Flue Integrity Test",
      "Condensate Termination",
      "Condensate Through Wall Greater Than 32mm",
    ],
  },
  {
    title: "Pipework",
    types: ["Gas Meter", "System Filter"],
  },
  {
    title: "Electrical",
    types: ["Bonding (Gas)", "Bonding (Water)", "Flex Termination", "Power Supply Connection", "Heating Controls"],
  },
  {
    title: "Testing and Readings",
    types: ["FGA Readings", "Gas Integrity Test with Gas Analyser"],
  },
  {
    title: "Optional / If Applicable",
    optional: true,
    types: ["Hot Water Tank", "Conversion Pipework", "Fuse Spur", "Cabling (Trunk/Clipped)"],
  },
];

const TILE_STYLE = { width: 72, height: 72, objectFit: "cover", borderRadius: 6, display: "block" };

// Tiles only need Strapi's small thumbnail; PDFs and small images don't have one
const tileUrl = (media) => media.formats?.thumbnail?.url || media.url;

export function InstallationImagesSection({
  jobToComplete,
  installationImageSignOffHandler,
  addInstallationImageToJob,
  removeInstallationFromJob,
  uploadingImages = [],
  errorMessage,
}) {
  const isComplete = jobToComplete.completionDetail.installationImagesComplete;
  const completionImages = jobToComplete.completionDetail.completionImages;
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);
  // The image type whose Camera/Upload button opened the file picker
  const [selectingType, setSelectingType] = useState(null);

  const handleTriggerInput = (type, useCamera) => {
    setSelectingType(type);
    if (useCamera) {
      cameraInputRef.current.click();
    } else {
      fileInputRef.current.click();
    }
  };

  // Upload as soon as a file is chosen
  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    // Clear the input so choosing the same file again still triggers a change
    e.target.value = "";
    if (file && selectingType) {
      addInstallationImageToJob(file, selectingType);
    }
    setSelectingType(null);
  };

  const getImagesForType = (type) => completionImages.filter((img) => img.imageTitle === type);

  return (
    <AccordionSection
      id="installationImages"
      headerId="headerInstallationImages"
      collapseId="collapseInstallationImages"
      number={6}
      title="Upload Installation Images"
      isComplete={isComplete}
    >
      {/* Shared hidden file inputs — outside all forms */}
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="d-none" onChange={handleFileChange} />
      <input ref={fileInputRef} type="file" accept="image/*, .pdf" className="d-none" onChange={handleFileChange} />

      <div className="d-flex justify-content-between align-items-center mb-3">
        <small className="text-muted"><span className="text-danger">*</span> Required image</small>
        <span className="badge bg-secondary">{completionImages.length} image{completionImages.length !== 1 ? "s" : ""} uploaded</span>
      </div>

      {IMAGE_GROUPS.map((group) => (
        <div key={group.title} className="mb-4">
          <div className={`px-2 py-1 rounded mb-3 ${group.optional ? "bg-warning bg-opacity-25" : "bg-light"}`}>
            <span className="fw-bold" style={{ fontSize: "0.9rem" }}>{group.title}</span>
            {group.optional && (
              <span className="text-muted fw-normal ms-2" style={{ fontSize: "0.8rem" }}>upload if applicable</span>
            )}
          </div>

          {group.types.map((type) => {
            const uploaded = getImagesForType(type);
            const uploading = uploadingImages.filter((image) => image.imageTitle === type);
            const isRequired = !group.optional;

            return (
              <div key={type} className="mb-3 pb-3 border-bottom">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <p className="mb-0 fw-medium" style={{ fontSize: "0.9rem" }}>
                    {type}
                    {isRequired && <span className="text-danger ms-1">*</span>}
                    {uploaded.length > 0 && (
                      <span className="badge bg-success ms-2" style={{ fontSize: "0.7rem" }}>{uploaded.length}</span>
                    )}
                  </p>
                  {!isComplete && (
                    <div className="d-flex gap-2 ms-2 flex-shrink-0">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => handleTriggerInput(type, true)}
                      >
                        Camera
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => handleTriggerInput(type, false)}
                      >
                        Upload
                      </button>
                    </div>
                  )}
                </div>

                {/* Thumbnails of uploaded images for this type */}
                {(uploaded.length > 0 || uploading.length > 0) && (
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    {uploaded.map((image) => (
                      <div key={image.id} className="position-relative">
                        {image.installationImage?.url ? (
                          <SignedImage src={tileUrl(image.installationImage)} style={TILE_STYLE} />
                        ) : (
                          // The file was deleted in Strapi; keep the entry so it can be removed
                          <div
                            className="bg-light border text-muted d-flex align-items-center justify-content-center small"
                            style={{ width: 72, height: 72, borderRadius: 6 }}
                          >
                            Missing
                          </div>
                        )}
                        {!isComplete && (
                          <button
                            className="btn btn-danger position-absolute d-flex align-items-center justify-content-center"
                            style={{ top: 2, right: 2, width: 20, height: 20, padding: 0, fontSize: "0.65rem", lineHeight: 1 }}
                            name="removeImage"
                            value={image.id}
                            onClick={removeInstallationFromJob}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                    {uploading.map((image) => (
                      <div key={image.key} className="position-relative" title="Uploading…">
                        {image.previewUrl ? (
                          <img src={image.previewUrl} alt="" style={{ ...TILE_STYLE, opacity: 0.5 }} />
                        ) : (
                          <div className="bg-light border text-muted small d-flex align-items-center justify-content-center" style={TILE_STYLE}>
                            PDF
                          </div>
                        )}
                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center">
                          <span className="spinner-border spinner-border-sm text-primary" role="status" aria-label="Uploading" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      <div className="d-flex flex-column flex-md-row justify-content-md-end gap-2 mt-3 pt-3 border-top">
        <button onClick={installationImageSignOffHandler} className="btn btn-primary" disabled={isComplete}>
          Submit All Installation Images
        </button>
      </div>
      <SectionErrors errorMessage={errorMessage} section="installationImages" />
    </AccordionSection>
  );
}

InstallationImagesSection.propTypes = {
  jobToComplete: PropTypes.object.isRequired,
  installationImageSignOffHandler: PropTypes.func.isRequired,
  addInstallationImageToJob: PropTypes.func.isRequired,
  removeInstallationFromJob: PropTypes.func.isRequired,
  uploadingImages: PropTypes.array,
  errorMessage: PropTypes.array.isRequired,
};
